import { isMobile } from '@farmasiet/frontendcore-reactcomponents';
import classNames from 'classnames';
import * as React from 'react';

import { PictureProfile } from '../../../ResizedImage/PictureProfile.csharp';
import { getTranslation, keys } from '../../../Resources/Translations';
import { Loader } from '../../Components/Loader/Loader';
import { SearchResultViewModel } from '../../Filtering/Api/SearchResultViewModel.csharp';
import { ProductSortingType } from '../../Filtering/Sorting/ProductSortingType.csharp';
import { RecommendedProducts } from '../../Recommendations/Components/RecommededProducts/RecommendedProducts';
import { RecommendedProductsProps } from '../../Recommendations/ViewModels/RecommendedProductsProps.csharp';
import { SearchItem } from './SearchItem';

interface SearchSuggestionsProps {
    isSticky: boolean;
    isLoading: boolean;
    searchResult: SearchResultViewModel;
    searchPhrase: string;
    lastSearchPhrase?: string;
    searchUrl: string;
    epiFindClickTrackingUrl: string;
    experimentProductClickUrl: string;
    pictureProfile: PictureProfile;
    getSearchPhraseUrl: (url: string, withoutSlash?: boolean, searchValue?: string) => string;
    unfocusInput: () => void;
    closeSearch: () => void;
    searchRecommendationsProps?: RecommendedProductsProps;
}

export const SearchSuggestions = (props: SearchSuggestionsProps) => {
    const {
        isSticky,
        searchResult,
        searchUrl,
        searchPhrase,
        lastSearchPhrase,
        getSearchPhraseUrl,
        pictureProfile,
        closeSearch,
        isLoading,
        epiFindClickTrackingUrl,
        experimentProductClickUrl,
        searchRecommendationsProps
    } = props;

    const suggestionsRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        suggestionsRef.current?.scrollTo(0, 0);
    }, [searchResult]);

    const handleClickOutside = () => {
        if (!isMobile()) {
            closeSearch();
        }
    };

    const formatSuggestionPhrase = (phrase: string, boldedFragment?: string): React.ReactNode => {
        if (!boldedFragment) return phrase;

        const index = phrase.toLowerCase().indexOf(boldedFragment.toLowerCase());

        if (index === -1) {
            return phrase;
        }

        const before = phrase.slice(0, index);
        const match = phrase.slice(index, index + boldedFragment.length);
        const after = phrase.slice(index + boldedFragment.length);

        return (
            <span>
                <span className="RichText__SuggestionPhrasesItemText RichText__SuggestionPhrasesItemText--Bold">{before}</span>
                <span className="RichText__SuggestionPhrasesItemText">{match}</span>
                <span className="RichText__SuggestionPhrasesItemText RichText__SuggestionPhrasesItemText--Bold">{after}</span>
            </span>
        );
    };

    const isSuggestionPhrasesSectionVisible = searchResult.suggestionPhrases && searchResult.suggestionPhrases.length > 0;
    const isProductsSectionVisible = searchResult.products && searchResult.products.length > 0;
    const isBrandsSectionVisible = searchResult.brandsLinks && searchResult.brandsLinks.length > 0;
    const isArticlesSectionVisible = searchResult.articlesLinks && searchResult.articlesLinks.length > 0;
    const isCategoriesSectionVisible = searchResult.categoriesLinks && searchResult.categoriesLinks.length > 0;

    const isBrandsAndCategoriesSectionVisible = isBrandsSectionVisible || isCategoriesSectionVisible;
    const isUpperSectionVisible = isSuggestionPhrasesSectionVisible || isProductsSectionVisible || isBrandsAndCategoriesSectionVisible;

    return (
        <>
            <div className={classNames('RichSearch__Background', isSticky && 'RichSearch__Background--Sticky')} onClick={handleClickOutside}>
                <div
                    className={classNames('RichSearch__Suggestions', isSticky && 'RichSearch__Suggestions--Sticky')}
                    ref={suggestionsRef}
                    onClick={(e) => e.stopPropagation()}
                    onScroll={props.unfocusInput}
                >
                    <Loader isLoading={isLoading} className={'RichSearch__Loader'} />
                    {isUpperSectionVisible && (
                        <div className="RichSearch__UpperSection">
                            {isSuggestionPhrasesSectionVisible && (
                                <section className="RichText__SuggestionPhrasesSection">
                                    <h3 className="RichSearch__SectionHeader RichText__SuggestionPhrasesSectionHeading">
                                        {getTranslation(keys['search.phrases'])}
                                    </h3>
                                    <ul className="RichText__SuggestionPhrasesList">
                                        {searchResult.suggestionPhrases.slice(0, 3).map((phrase, idx) => (
                                            <li key={idx} className="RichText__SuggestionPhrasesItem">
                                                <a href={getSearchPhraseUrl(searchUrl, false, phrase)}>{formatSuggestionPhrase(phrase, lastSearchPhrase)}</a>
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}
                            {isProductsSectionVisible && (
                                <section>
                                    <div className="RichSearch__ResultsHeader">
                                        <h3 className="RichSearch__SectionHeader">{getTranslation(keys['search.products'])}</h3>
                                        <a href={getSearchPhraseUrl(searchUrl)} className="RichSearch__SeeAll">
                                            {getTranslation(keys['search.seeAll'])} ({searchResult.totalCount})
                                        </a>
                                    </div>
                                    <ul className="RichSearch__Results">
                                        {searchResult.products.map((item) => (
                                            <SearchItem
                                                key={item.code}
                                                item={item}
                                                pictureProfile={pictureProfile}
                                                sortingType={ProductSortingType.ByRelevance}
                                                experimentProductClickUrl={experimentProductClickUrl}
                                                epiFindClickTrackingUrl={epiFindClickTrackingUrl}
                                            />
                                        ))}
                                    </ul>
                                </section>
                            )}
                            {isBrandsAndCategoriesSectionVisible && (
                                <div className="RichSearch__SectionsWrapper">
                                    {isBrandsSectionVisible && (
                                        <section className="RichSearch__Section">
                                            <h3 className="RichSearch__SectionHeader RichSearch__SectionHeader--BigMargin">
                                                {getTranslation(keys['search.brands'])}
                                            </h3>
                                            <ul className="RichSearch__Results">
                                                {searchResult.brandsLinks.map((item) => (
                                                    <li className={'RichSearch__Link'} key={item.href}>
                                                        <a href={item.href}>{item.text}</a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </section>
                                    )}
                                    {isCategoriesSectionVisible && (
                                        <section className="RichSearch__Section">
                                            <h3 className="RichSearch__SectionHeader RichSearch__SectionHeader--BigMargin">
                                                {getTranslation(keys['search.categories'])}
                                            </h3>
                                            <ul className="RichSearch__Results">
                                                {searchResult.categoriesLinks.map((item) => (
                                                    <li className={'RichSearch__Link'} key={item.href}>
                                                        <a href={item.href}>{item.text}</a>
                                                    </li>
                                                ))}
                                            </ul>
                                        </section>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {!isUpperSectionVisible && !isLoading && searchPhrase && (
                        <section className="RichSearch__NoResults">
                            <h3 className="RichSearch__SectionHeader">{getTranslation(keys['search.products'])}</h3>
                            <span className="RichSearch__NoResultsHeading">{getTranslation(keys['search.noResults'])}</span>
                            {searchRecommendationsProps ? (
                                <>
                                    <span className="RichSearch__NoResultsSubheading">{getTranslation(keys['search.seeRecommendations'])}</span>
                                    <ul className="RichSearch__AdditionalContent">
                                        <RecommendedProducts
                                            variant={searchRecommendationsProps.variant}
                                            variantMobile={searchRecommendationsProps.variantMobile}
                                            productsUrl={searchRecommendationsProps.productsUrl}
                                            epiFindClickTrackingUrl={searchRecommendationsProps.epiFindClickTrackingUrl}
                                            experimentProductClickUrl={searchRecommendationsProps.experimentProductClickUrl}
                                            profile={searchRecommendationsProps.profile}
                                            cartItemUrl={searchRecommendationsProps.cartItemUrl}
                                            validateItemUrl={searchRecommendationsProps.validateItemUrl}
                                            rxUrl={searchRecommendationsProps.rxUrl}
                                            model={searchRecommendationsProps.model}
                                        />
                                    </ul>
                                </>
                            ) : (
                                <span className="RichSearch__NoResultsSubheading">{getTranslation(keys['search.checkSpelling'])}</span>
                            )}
                        </section>
                    )}

                    {isArticlesSectionVisible && (
                        <div className="RichSearch__LowerSection RichSearch__Articles">
                            <h3 className="RichSearch__SectionHeader">{getTranslation(keys['search.pharmacyAdvice'])}</h3>
                            <ul className="RichSearch__Results RichSearch__Results--Horizontal">
                                {searchResult.articlesLinks.map((item) => (
                                    <li className={'RichSearch__Link'} key={item.href}>
                                        <a href={item.href}>{item.text}</a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};
